import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";


const Vehicle_dieselkm_report_hire = () => {
  useAuthRedirect();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(localStorage.getItem("branch") || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false); // Loading state
  const navigate = useNavigate();

  const canView  = hasPermission("Vehicle Monthly Diesel Km Consumption Report", "view");

  useEffect(() => {
    axios.get("https://rfs2.infraconsoft.net.in/api/vehicle_dieselkms_report_hire")
      .then((response) => {
        setBranches(response.data.branches);
      })
      .catch((error) => {
        console.error("Error fetching branches:", error);
      });
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      setLoading(true); // Start loading
      axios.get(`https://rfs2.infraconsoft.net.in/api/vehicle_dieselkms_report_hire?branch_code=${selectedBranch}`)
        .then((response) => {
          setData(response.data.vehicles);
          setFilteredData(response.data.vehicles);
        })
        .catch((error) => {
          console.error("Error fetching vehicles:", error);
        })
        .finally(() => {
          setLoading(false); // Stop loading
        });
    }
  }, [selectedBranch]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    const filtered = data.filter((item) =>
      (item.veh_name && item.veh_name.toLowerCase().includes(e.target.value.toLowerCase())) ||
      (item.veh_code && item.veh_code.toLowerCase().includes(e.target.value.toLowerCase())) ||
      (item.veh_type && item.veh_type.toLowerCase().includes(e.target.value.toLowerCase()))
    );
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const [site, setSite] = useState({
  ok: true,
  name: "Infraconsoft",
  logo: null,
  address: "",
  email: "",
  phone: "",
  _ts: 0,
});

useEffect(() => {
  let ignore = false;
  (async () => {
    try {
      const res = await axios.get("https://rfs2.infraconsoft.net.in/api/siteinfo");
      const s = res?.data || {};
      let logo = s.logo || null;
      if (logo && !/^https?:\/\//i.test(logo)) {
        if (!logo.startsWith("/")) logo = `/${logo}`;
        logo = `${window.location.origin}${logo}`;
      }
      if (!ignore) {
        setSite({
          ok: !!s.ok,
          name: s.name || "Infraconsoft",
          logo,
          address: s.address || "",
          email: s.email || "",
          phone: s.phone || "",
          _ts: Date.now(),
        });
      }
    } catch (e) {
      console.error("Failed to load /siteinfo", e);
    }
  })();
  return () => {
    ignore = true;
  };
}, []);

const exportToExcel = () => {
  const months = ["APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC", "JAN", "FEB", "MAR"];

  // 1️⃣ Build data rows (Diesel per KM each month)
  const exportData = data.map((item, index) => {
    const monthData = {};
    months.forEach((m) => {
      const km = Number(item[`${m}Km`] || 0);
      const ds = Number(item[`${m}Ds`] || 0);
      monthData[m] = km > 0 && ds > 0 ? (ds / km).toFixed(2) : "-"; // Diesel per KM
    });

    return {
      "S.No": index + 1,
      Branch: selectedBranch,
      "Vehicle Name": item.veh_name,
      "Vehicle Code": item.veh_code,
      "Vehicle Type": item.veh_type,
      ...monthData,
    };
  });

  // 2️⃣ Preserve your column order
  const headers = exportData.length
    ? Object.keys(exportData[0])
    : ["S.No", "Branch", "Vehicle Name", "Vehicle Code", "Vehicle Type", ...months];
  const dataRows = exportData.map(r => headers.map(h => r[h] ?? ""));

  // 3️⃣ Company header + title (merged across columns)
  const COLS = headers.length;
  const fullWidth = (text = "") => [text, ...Array(COLS - 1).fill("")];

  const titleBits = ["Vehicle Diesel KMs Report (Hire) — Diesel/KM"];
  if (selectedBranch) titleBits.push(`Branch: ${selectedBranch}`);
  const title = titleBits.join(" — ");

  const aoa = [
    fullWidth((site.name || "Infraconsoft").toUpperCase()),
    fullWidth(site.address || ""),
    fullWidth(
      [site.phone ? `Ph: ${site.phone}` : "", site.email ? `Email: ${site.email}` : ""]
        .filter(Boolean)
        .join(" | ")
    ),
    [], // spacer
    fullWidth(title),
    [], // spacer
    headers,
    ...dataRows,
  ];

  // 4️⃣ Create worksheet with merged header and formatting
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } }, // company name
    { s: { r: 1, c: 0 }, e: { r: 1, c: COLS - 1 } }, // address
    { s: { r: 2, c: 0 }, e: { r: 2, c: COLS - 1 } }, // phone/email
    { s: { r: 4, c: 0 }, e: { r: 4, c: COLS - 1 } }, // title
  ];

  // 5️⃣ Column widths
  const widthMap = {
    "S.No": 6,
    "Branch": 16,
    "Vehicle Name": 22,
    "Vehicle Code": 14,
    "Vehicle Type": 14,
  };
  ws["!cols"] = headers.map(h => ({ wch: widthMap[h] || 10 }));

  // 6️⃣ Freeze header row
  ws["!freeze"] = { xSplit: 0, ySplit: 6, topLeftCell: "A7", activePane: "bottomLeft" };

  // 7️⃣ Build workbook and save
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Vehicle Diesel Report");

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });

  saveAs(blob, `vehicle_dieselkms_report_hire_${selectedBranch || "all"}.xlsx`);
};


  const handleSelection = (event) => {
    const value = event.target.value;
    if (value) {
      navigate('/vehicle_dieselkms_report');
    }
  };


  if (!canView) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <h2 className="add-txt">Vehicle-Monthly Consumption Report - (Monthly Km / Diesel)</h2>
          <div>
            <Time />
          </div>
        </section>

        <div className="add-new-container">
          <button className="add-new-btn" onClick={exportToExcel}>
            <i className="fa-solid fa-file-excel"></i> Export to Excel
          </button>
        </div>

              <div className="vehicle-class">
  <div>
    <p>Branch wise Report :</p>
                    <select value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)} required className="branch-select">
            <option value="">Select Branch</option>
            {branches.map((item) => (
              <option key={item.branchMaster_code} value={item.branchMaster_code}>{item.branchMaster_name}</option>
            ))}
          </select>
  </div>

  <div>
    <p>Select your vehicle:</p>
    <select className="branch-select" onChange={handleSelection}>     
      <option value="hire">Hire Vehicle</option>
     <option value="own">Own Vehicle</option>
            
    </select>
  </div>
</div>





        <div className="search-filter">
          <p>Search: </p>
          <input type="text" placeholder="Search by vehicleName" value={searchTerm} onChange={handleSearch} className="search-input" />
        </div>

        <div className="content-table">
  <table>
    <thead>
      <tr>
        <th>S.No</th>
        <th>Branch</th>
        <th>Vehicle Name</th>
        <th>Vehicle Code</th>
        <th>Vehicle Type</th>
        <th>APR</th>
        <th>MAY</th>
        <th>JUN</th>
        <th>JUL</th>
        <th>AUG</th>
        <th>SEP</th>
        <th>OCT</th>
        <th>NOV</th>
        <th>DEC</th>
        <th>JAN</th>
        <th>FEB</th>
        <th>MAR</th>
      </tr>
    </thead>
    <tbody>
      {loading ? (
        <tr>
          <td colSpan="17" className="loading-text">Loading data, please wait...</td>
        </tr>
      ) : currentData.length > 0 ? (
        currentData.map((item, index) => {
          const months = ["APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC", "JAN", "FEB", "MAR"];
          return (
            <tr key={index}>
              <td>{indexOfFirstItem + index + 1}</td>
              <td>{selectedBranch}</td>
              <td>{item.veh_name}</td>
              <td>{item.veh_code}</td>
              <td>{item.veh_type}</td>
              {months.map((month) => {
                const Km = item[`${month}Km`] || 0;
                const ds = item[`${month}Ds`] || 0;
                return (
                  <td key={month}>
                    {Km > 0 && ds > 0 ? (ds / Km).toFixed(2) : "-"}
                  </td>
                );
              })}
            </tr>
          );
        })
      ) : (
        <tr>
          <td colSpan="17">No Data Available</td>
        </tr>
      )}
    </tbody>
  </table>
</div>


<div className="pagination">
          <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
            Previous
          </button>

          {/* Always show first page */}
          <button onClick={() => paginate(1)} className={currentPage === 1 ? 'active' : ''}>
            1
          </button>

          {/* Show up to 3 pages dynamically */}
          {filteredData.length > 0 &&
            [...Array(Math.min(3, Math.ceil(filteredData.length / itemsPerPage) - 1))].map((_, index) => {
              const pageNumber = currentPage + index;
              if (pageNumber > 1 && pageNumber <= Math.ceil(filteredData.length / itemsPerPage)) {
                return (
                  <button key={pageNumber} onClick={() => paginate(pageNumber)} className={currentPage === pageNumber ? 'active' : ''}>
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })
          }

          {/* Next button */}
          <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}>
            Next
          </button>
        </div>


      </div>
    </div>
  );
};

export default Vehicle_dieselkm_report_hire;
